import {useEffect, useRef, useState} from "react";
import {connect} from "react-redux";
import {bindActionCreators, Dispatch} from "redux";

import {FormActions} from "@pg-mono/form";
import {isEqual} from "@pg-mono/nodash";
import {appendQueryString} from "@pg-mono/request";
import {rpAppLink, rpAppPath} from "@pg-mono/rp-routes";

import {IRPStore} from "../../app/rp_reducer";
import {DesktopBlend} from "../../atoms/DesktopBlend";
import {useSubscribeToNewsletter} from "../../newsletter/hooks/useSubscribeToNewsletter";
import {createOfferLink} from "../../offer/helpers/create_offer_link";
import {addRegionSlugToQuery} from "../../offer/helpers/friendly_offer_list/add_region_slug_to_query";
import {offerUrlBuilder} from "../../offer/helpers/friendly_offer_list/offer_url_builder";
import {DEFAULT_DISTANCE_FOR_PLACES_TAB} from "../../offer/list/constants/offer_list";
import {Country} from "../../region/types/Country";
import {getHomeMatchSearchRegions} from "../../region/utils/get_homematch_regions";
import {fetchAllSearchLists} from "../../search/actions/fetch_search_all_action";
import {searchFormActions} from "../../search/actions/search_form_actions";
import {resetActiveDropdownItem} from "../../search/actions/set_active_dropdown_item";
import {updateCurrentTab} from "../../search/actions/update_current_tab";
import {ISearchInputValue} from "../../search/components/ISearchInputValue";
import {ISearchAutocompleteFormValues} from "../../search/components/search_autocomplete/SearchAutocomplete";
import {searchFormFields} from "../../search/constants/search_form_constants";
import {useHitSearchSubmit} from "../../search/hooks/use_hit_search_submit";
import {useTrackAmplitudeGlobalSearch} from "../../search/tracking/use_track_amplitude_global_search";
import {SearchFormValuesType} from "../../search/types/SearchFormValuesType";
import {SearchTab} from "../../search/types/SearchTab";
import {getOffersCountFromSearchValue} from "../../search/utils/get_offers_count_from_search_value";
import {getStringQueryValuesFromFormValues} from "../../search/utils/get_string_values_from_form_values";
import {saveRecentFilters} from "../../search/utils/recent_filters_storage";
import {validateQueryValues} from "../../search/utils/validate_query_values";
import {BoundAction} from "../../store/utils/bound_action";
import {searchSubmitAlgolytics} from "../../tracking/algolytics_hits/search_submit_hit";
import {debouncedSearchTypingHit} from "../../tracking/algolytics_hits/search_typing_hit";
import {getTrackedSiteData} from "../../tracking/algolytics_hits/tracked_utils/tracked_site";
import {useHomeMatchPreferencesStorage} from "../../user/hooks/preferences/use_home_match_preferences_storage";
import {HomepageHeroSearch} from "./HomepageSearch/HomepageHeroSearch";
import {HomepageLegacySearch} from "./HomepageSearch/HomepageLegacySearch";
import {HomepageSearchLayoutVariant, HomepageSearchMobileSelectionMode, HomepageSearchSubmitMode} from "./HomepageSearch/HomepageSearchTypes";

interface IStateProps {
    formValues: SearchFormValuesType;
}

interface IActionsProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formActions: FormActions<Record<keyof typeof searchFormFields, any>>;
    resetActiveDropdownItem: typeof resetActiveDropdownItem;
    updateCurrentTab: typeof updateCurrentTab;
    fetchAllSearchLists: BoundAction<typeof fetchAllSearchLists>;
}

interface IOwnProps {
    blendTopPosition: string;
    containerHeight: string;
    disableMultiRegion?: boolean;
    autoFocus?: boolean;
    layoutVariant?: HomepageSearchLayoutVariant;
    submitMode?: HomepageSearchSubmitMode;
    mobileSelectionMode?: HomepageSearchMobileSelectionMode;
}

interface IProps extends IOwnProps, IStateProps, IActionsProps {}

const HomepageSearchC = (props: IProps) => {
    const {
        formValues,
        blendTopPosition,
        containerHeight,
        disableMultiRegion,
        autoFocus,
        fetchAllSearchLists,
        formActions,
        resetActiveDropdownItem,
        updateCurrentTab
    } = props;
    const layoutVariant = props.layoutVariant ?? "legacy";
    const submitMode = props.submitMode ?? "auto";
    const mobileSelectionMode = props.mobileSelectionMode ?? "autoSubmit";
    const isManualSubmit = submitMode === "manual";

    const [isMobileSearchActive, setMobileSearchActive] = useState(false);
    const [isDesktopSearchBlendActive, setDesktopSearchBlendActive] = useState(false);
    const [windowHeight, setWindowHeight] = useState(0);
    const mobileHeroAutoSubmitStartedRef = useRef(false);
    const hitSearchSubmit = useHitSearchSubmit();
    const {trackAmplitudeSearch, buildAmplitudeSearchQuery, firePendingAmplitudeSearch, clearPendingAmplitudeSearch} = useTrackAmplitudeGlobalSearch();
    const {putPreferences} = useHomeMatchPreferencesStorage();
    const {saveNewsletterSearchData} = useSubscribeToNewsletter();
    useEffect(() => {
        setWindowHeight(document.body.clientHeight);
    }, [isDesktopSearchBlendActive]);

    useEffect(() => {
        if (layoutVariant === "hero") {
            updateCurrentTab(SearchTab.Regions, Country.POLAND);
        }
    }, [layoutVariant, updateCurrentTab]);

    const onSubmit = async (formValues: SearchFormValuesType) => {
        // Fire the event staged by a popular city click; fall back to manual_input when the
        // user reached this submit by typing a region directly into the search input.
        const firedPending = firePendingAmplitudeSearch();
        if (!firedPending) {
            trackAmplitudeSearch({
                searchQuery: buildAmplitudeSearchQuery(formValues.search as ISearchInputValue | undefined),
                searchMethod: "manual_input",
                offersCount: getOffersCountFromSearchValue(formValues.search as ISearchInputValue | undefined)
            });
        }
        const searchValue = formValues["search"] as ISearchInputValue | undefined;

        if (!canSubmitSearchValue(searchValue)) {
            return;
        }

        const selectedRegions = getSelectedSearchRegions(searchValue);

        saveNewsletterSearchData(formValues);
        putPreferences({
            searchRegions: getHomeMatchSearchRegions(selectedRegions)
        });

        searchSubmitAlgolytics({...getTrackedSiteData()});

        hitSearchSubmit(formValues);

        // A location-only search counts as a recent search too — the filter
        // drawers save on their own submit, but a plain region search would
        // otherwise never reach the personalized homepage.
        const isAbroadRegionSearch = selectedRegions[0] && selectedRegions[0].country != Country.POLAND;

        if (searchValue?.tabType === SearchTab.Regions && !isAbroadRegionSearch) {
            saveRecentFilters(formValues);
        }

        const queryValuesRaw = getStringQueryValuesFromFormValues(formValues);
        let queryValues = validateQueryValues(queryValuesRaw);

        if (searchValue?.tabType === SearchTab.Offers) {
            return (window.location.href = createOfferLink({
                ...searchValue.offer,
                vendor: {slug: searchValue.vendor.slug}
            }));
        }
        if (searchValue?.tabType === SearchTab.Vendors) {
            return (window.location.href = rpAppLink.vendor.detail.base({
                vendorSlug: searchValue.vendor.slug,
                vendorId: searchValue.vendor.id
            }));
        }

        if (searchValue?.tabType === SearchTab.Places) {
            const placeLinkQuery = {
                lat: searchValue.location.lat,
                lng: searchValue.location.lng,
                s: searchValue.place.description,
                distance: DEFAULT_DISTANCE_FOR_PLACES_TAB
            };
            return (window.location.href = appendQueryString(rpAppPath.offer.list, placeLinkQuery));
        }

        if (searchValue?.tabType === SearchTab.Regions && searchValue.regions.length > 1) {
            return (window.location.href = appendQueryString(rpAppLink.offer.multiList(), queryValues));
        }

        if (searchValue?.tabType === SearchTab.Regions && searchValue.regions[0]) {
            queryValues = addRegionSlugToQuery(queryValues, searchValue.regions[0]);
        }

        const {friendlySlug} = offerUrlBuilder(queryValues);

        const region = Array.isArray(queryValues.region) ? queryValues.region[0] : queryValues.region;
        const firstRegion = selectedRegions[0];
        const isAbroad = firstRegion && firstRegion.country != Country.POLAND;
        if (isAbroad && firstRegion.country) {
            const {friendlySlug, countrySlug} = offerUrlBuilder({region: "", country: firstRegion.country});
            const abroadSubcategoryLink = rpAppLink.offer.listFriendlyAbroad({
                friendlySlug: friendlySlug,
                country: countrySlug,
                abroadRegionSlug: region
            });
            return (window.location.href = abroadSubcategoryLink);
        }

        const url = rpAppLink.offer.listFriendly({friendlySlug});

        return (window.location.href = url);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onAfterChange = (name?: string, value?: any) => {
        if (isManualSubmit) {
            return;
        }

        const currentValues = name ? {...formValues, [name]: value} : formValues;
        const queryValues = getStringQueryValuesFromFormValues(currentValues);

        if (isEqual(queryValues, {}) || !canSubmitSearchValue(currentValues.search as ISearchInputValue | undefined)) {
            return;
        }

        return onSubmit(currentValues);
    };

    const onMobileHeroAfterChange = (name?: string, value?: unknown) => {
        const currentValues = (name ? {...formValues, [name]: value} : formValues) as SearchFormValuesType;
        const searchValue = currentValues.search as ISearchInputValue | undefined;

        if (shouldSelectAndCloseMobileSuggestion(searchValue, mobileSelectionMode)) {
            return;
        }

        if (mobileHeroAutoSubmitStartedRef.current || !canAutoSubmitSelectedSearchValue(searchValue)) {
            return;
        }

        mobileHeroAutoSubmitStartedRef.current = true;
        void onSubmit(currentValues);
    };

    /**
     * Handle filters
     */

    const onChange = (values: Partial<ISearchAutocompleteFormValues>) => {
        if (!!values.search?.label && formValues.search.label !== values.search.label) {
            debouncedSearchTypingHit({query: values.search.label});
            // Discard a popular-city pending so a subsequent manual submit doesn't fire popular_search_click.
            clearPendingAmplitudeSearch();
        }

        formActions.update(values);
    };

    const onMobileHeroChange = (values: Partial<ISearchAutocompleteFormValues>) => {
        if (shouldSelectAndCloseMobileSuggestion(values.search, mobileSelectionMode)) {
            onChange(values);
            setMobileSearchActive(false);
            return;
        }

        if (values.search && canAutoSubmitSelectedSearchValue(values.search)) {
            onMobileHeroAfterChange("search", values.search);
            return;
        }

        onChange(values);
    };

    const onAfterTabSwitch = () => {
        resetActiveDropdownItem();
    };

    const onSearchSubmit = () => {
        void onSubmit(formValues);
    };

    const isSearchButtonDisabled = !canSubmitSearchValue(formValues.search as ISearchInputValue | undefined);
    const autocompleteSubmit = isManualSubmit ? onSearchSubmit : undefined;
    const searchViewProps = {
        formValues,
        disableMultiRegion,
        autoFocus,
        mobileSelectionMode,
        fetchAllSearchLists,
        onChange,
        onMobileHeroChange,
        onAfterChange,
        onMobileHeroAfterChange,
        onAfterTabSwitch,
        onSearchSubmit,
        autocompleteSubmit,
        isSearchButtonDisabled,
        isMobileSearchActive,
        setMobileSearchActive,
        setDesktopSearchBlendActive
    };

    return (
        <>
            <DesktopBlend
                top={blendTopPosition}
                heightDecrease={containerHeight}
                isActive={isDesktopSearchBlendActive}
                pageHeight={`${windowHeight}px`}
                isViewportWidth={layoutVariant === "hero"}
            />

            {layoutVariant === "hero" ? <HomepageHeroSearch {...searchViewProps} /> : <HomepageLegacySearch {...searchViewProps} />}
        </>
    );
};

export const HomepageSearch = connect(mapStateToProps, mapActionsToProps)(HomepageSearchC);

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.search.formValues as SearchFormValuesType
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators(
            {
                resetActiveDropdownItem,
                updateCurrentTab,
                fetchAllSearchLists
            },
            dispatch
        )
    };
}

export const canSubmitSearchValue = (searchValue?: ISearchInputValue) => {
    const hasTypedUnselectedText = Boolean(searchValue?.label?.trim());

    if (!hasTypedUnselectedText) {
        return true;
    }

    switch (searchValue?.tabType) {
        case SearchTab.Regions:
            return searchValue.regions.length > 0;
        case SearchTab.Offers:
            return Boolean(searchValue.offer);
        case SearchTab.Places:
            return Boolean(searchValue.place && searchValue.location);
        case SearchTab.Vendors:
            return Boolean(searchValue.vendor);
        default:
            return false;
    }
};

export const canAutoSubmitSelectedSearchValue = (searchValue?: ISearchInputValue) => {
    switch (searchValue?.tabType) {
        case SearchTab.Regions:
            return searchValue.regions.length > 0;
        case SearchTab.Offers:
            return Boolean(searchValue.offer);
        case SearchTab.Places:
            return Boolean(searchValue.place && searchValue.location);
        case SearchTab.Vendors:
            return Boolean(searchValue.vendor);
        default:
            return false;
    }
};

const getSelectedSearchRegions = (searchValue?: ISearchInputValue) => {
    if (searchValue?.tabType === SearchTab.Regions) {
        return searchValue.regions;
    }

    return [];
};

const shouldSelectAndCloseMobileSuggestion = (searchValue: ISearchInputValue | undefined, mobileSelectionMode: HomepageSearchMobileSelectionMode) => {
    return mobileSelectionMode === "selectAndClose" && canAutoSubmitSelectedSearchValue(searchValue);
};
import {useEffect, useRef, useState} from "react";
import {connect} from "react-redux";
import {bindActionCreators, Dispatch} from "redux";

import {FormActions} from "@pg-mono/form";
import {isEqual} from "@pg-mono/nodash";
import {appendQueryString} from "@pg-mono/request";
import {rpAppLink, rpAppPath} from "@pg-mono/rp-routes";

import {IRPStore} from "../../app/rp_reducer";
import {DesktopBlend} from "../../atoms/DesktopBlend";
import {useSubscribeToNewsletter} from "../../newsletter/hooks/useSubscribeToNewsletter";
import {createOfferLink} from "../../offer/helpers/create_offer_link";
import {addRegionSlugToQuery} from "../../offer/helpers/friendly_offer_list/add_region_slug_to_query";
import {offerUrlBuilder} from "../../offer/helpers/friendly_offer_list/offer_url_builder";
import {DEFAULT_DISTANCE_FOR_PLACES_TAB} from "../../offer/list/constants/offer_list";
import {Country} from "../../region/types/Country";
import {getHomeMatchSearchRegions} from "../../region/utils/get_homematch_regions";
import {fetchAllSearchLists} from "../../search/actions/fetch_search_all_action";
import {searchFormActions} from "../../search/actions/search_form_actions";
import {resetActiveDropdownItem} from "../../search/actions/set_active_dropdown_item";
import {updateCurrentTab} from "../../search/actions/update_current_tab";
import {ISearchInputValue} from "../../search/components/ISearchInputValue";
import {ISearchAutocompleteFormValues} from "../../search/components/search_autocomplete/SearchAutocomplete";
import {searchFormFields} from "../../search/constants/search_form_constants";
import {useHitSearchSubmit} from "../../search/hooks/use_hit_search_submit";
import {useTrackAmplitudeGlobalSearch} from "../../search/tracking/use_track_amplitude_global_search";
import {SearchFormValuesType} from "../../search/types/SearchFormValuesType";
import {SearchTab} from "../../search/types/SearchTab";
import {getOffersCountFromSearchValue} from "../../search/utils/get_offers_count_from_search_value";
import {getStringQueryValuesFromFormValues} from "../../search/utils/get_string_values_from_form_values";
import {saveRecentFilters} from "../../search/utils/recent_filters_storage";
import {validateQueryValues} from "../../search/utils/validate_query_values";
import {BoundAction} from "../../store/utils/bound_action";
import {searchSubmitAlgolytics} from "../../tracking/algolytics_hits/search_submit_hit";
import {debouncedSearchTypingHit} from "../../tracking/algolytics_hits/search_typing_hit";
import {getTrackedSiteData} from "../../tracking/algolytics_hits/tracked_utils/tracked_site";
import {useHomeMatchPreferencesStorage} from "../../user/hooks/preferences/use_home_match_preferences_storage";
import {HomepageHeroSearch} from "./HomepageSearch/HomepageHeroSearch";
import {HomepageLegacySearch} from "./HomepageSearch/HomepageLegacySearch";
import {HomepageSearchLayoutVariant, HomepageSearchMobileSelectionMode, HomepageSearchSubmitMode} from "./HomepageSearch/HomepageSearchTypes";

interface IStateProps {
    formValues: SearchFormValuesType;
}

interface IActionsProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formActions: FormActions<Record<keyof typeof searchFormFields, any>>;
    resetActiveDropdownItem: typeof resetActiveDropdownItem;
    updateCurrentTab: typeof updateCurrentTab;
    fetchAllSearchLists: BoundAction<typeof fetchAllSearchLists>;
}

interface IOwnProps {
    blendTopPosition: string;
    containerHeight: string;
    disableMultiRegion?: boolean;
    autoFocus?: boolean;
    layoutVariant?: HomepageSearchLayoutVariant;
    submitMode?: HomepageSearchSubmitMode;
    mobileSelectionMode?: HomepageSearchMobileSelectionMode;
}

interface IProps extends IOwnProps, IStateProps, IActionsProps {}

const HomepageSearchC = (props: IProps) => {
    const {
        formValues,
        blendTopPosition,
        containerHeight,
        disableMultiRegion,
        autoFocus,
        fetchAllSearchLists,
        formActions,
        resetActiveDropdownItem,
        updateCurrentTab
    } = props;
    const layoutVariant = props.layoutVariant ?? "legacy";
    const submitMode = props.submitMode ?? "auto";
    const mobileSelectionMode = props.mobileSelectionMode ?? "autoSubmit";
    const isManualSubmit = submitMode === "manual";

    const [isMobileSearchActive, setMobileSearchActive] = useState(false);
    const [isDesktopSearchBlendActive, setDesktopSearchBlendActive] = useState(false);
    const [windowHeight, setWindowHeight] = useState(0);
    const mobileHeroAutoSubmitStartedRef = useRef(false);
    const hitSearchSubmit = useHitSearchSubmit();
    const {trackAmplitudeSearch, buildAmplitudeSearchQuery, firePendingAmplitudeSearch, clearPendingAmplitudeSearch} = useTrackAmplitudeGlobalSearch();
    const {putPreferences} = useHomeMatchPreferencesStorage();
    const {saveNewsletterSearchData} = useSubscribeToNewsletter();
    useEffect(() => {
        setWindowHeight(document.body.clientHeight);
    }, [isDesktopSearchBlendActive]);

    useEffect(() => {
        if (layoutVariant === "hero") {
            updateCurrentTab(SearchTab.Regions, Country.POLAND);
        }
    }, [layoutVariant, updateCurrentTab]);

    const onSubmit = async (formValues: SearchFormValuesType) => {
        // Fire the event staged by a popular city click; fall back to manual_input when the
        // user reached this submit by typing a region directly into the search input.
        const firedPending = firePendingAmplitudeSearch();
        if (!firedPending) {
            trackAmplitudeSearch({
                searchQuery: buildAmplitudeSearchQuery(formValues.search as ISearchInputValue | undefined),
                searchMethod: "manual_input",
                offersCount: getOffersCountFromSearchValue(formValues.search as ISearchInputValue | undefined)
            });
        }
        const searchValue = formValues["search"] as ISearchInputValue | undefined;

        if (!canSubmitSearchValue(searchValue)) {
            return;
        }

        const selectedRegions = getSelectedSearchRegions(searchValue);

        saveNewsletterSearchData(formValues);
        putPreferences({
            searchRegions: getHomeMatchSearchRegions(selectedRegions)
        });

        searchSubmitAlgolytics({...getTrackedSiteData()});

        hitSearchSubmit(formValues);

        // A location-only search counts as a recent search too — the filter
        // drawers save on their own submit, but a plain region search would
        // otherwise never reach the personalized homepage.
        const isAbroadRegionSearch = selectedRegions[0] && selectedRegions[0].country != Country.POLAND;

        if (searchValue?.tabType === SearchTab.Regions && !isAbroadRegionSearch) {
            saveRecentFilters(formValues);
        }

        const queryValuesRaw = getStringQueryValuesFromFormValues(formValues);
        let queryValues = validateQueryValues(queryValuesRaw);

        if (searchValue?.tabType === SearchTab.Offers) {
            return (window.location.href = createOfferLink({
                ...searchValue.offer,
                vendor: {slug: searchValue.vendor.slug}
            }));
        }
        if (searchValue?.tabType === SearchTab.Vendors) {
            return (window.location.href = rpAppLink.vendor.detail.base({
                vendorSlug: searchValue.vendor.slug,
                vendorId: searchValue.vendor.id
            }));
        }

        if (searchValue?.tabType === SearchTab.Places) {
            const placeLinkQuery = {
                lat: searchValue.location.lat,
                lng: searchValue.location.lng,
                s: searchValue.place.description,
                distance: DEFAULT_DISTANCE_FOR_PLACES_TAB
            };
            return (window.location.href = appendQueryString(rpAppPath.offer.list, placeLinkQuery));
        }

        if (searchValue?.tabType === SearchTab.Regions && searchValue.regions.length > 1) {
            return (window.location.href = appendQueryString(rpAppLink.offer.multiList(), queryValues));
        }

        if (searchValue?.tabType === SearchTab.Regions && searchValue.regions[0]) {
            queryValues = addRegionSlugToQuery(queryValues, searchValue.regions[0]);
        }

        const {friendlySlug} = offerUrlBuilder(queryValues);

        const region = Array.isArray(queryValues.region) ? queryValues.region[0] : queryValues.region;
        const firstRegion = selectedRegions[0];
        const isAbroad = firstRegion && firstRegion.country != Country.POLAND;
        if (isAbroad && firstRegion.country) {
            const {friendlySlug, countrySlug} = offerUrlBuilder({region: "", country: firstRegion.country});
            const abroadSubcategoryLink = rpAppLink.offer.listFriendlyAbroad({
                friendlySlug: friendlySlug,
                country: countrySlug,
                abroadRegionSlug: region
            });
            return (window.location.href = abroadSubcategoryLink);
        }

        const url = rpAppLink.offer.listFriendly({friendlySlug});

        return (window.location.href = url);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onAfterChange = (name?: string, value?: any) => {
        if (isManualSubmit) {
            return;
        }

        const currentValues = name ? {...formValues, [name]: value} : formValues;
        const queryValues = getStringQueryValuesFromFormValues(currentValues);

        if (isEqual(queryValues, {}) || !canSubmitSearchValue(currentValues.search as ISearchInputValue | undefined)) {
            return;
        }

        return onSubmit(currentValues);
    };

    const onMobileHeroAfterChange = (name?: string, value?: unknown) => {
        const currentValues = (name ? {...formValues, [name]: value} : formValues) as SearchFormValuesType;
        const searchValue = currentValues.search as ISearchInputValue | undefined;

        if (shouldSelectAndCloseMobileSuggestion(searchValue, mobileSelectionMode)) {
            return;
        }

        if (mobileHeroAutoSubmitStartedRef.current || !canAutoSubmitSelectedSearchValue(searchValue)) {
            return;
        }

        mobileHeroAutoSubmitStartedRef.current = true;
        void onSubmit(currentValues);
    };

    /**
     * Handle filters
     */

    const onChange = (values: Partial<ISearchAutocompleteFormValues>) => {
        if (!!values.search?.label && formValues.search.label !== values.search.label) {
            debouncedSearchTypingHit({query: values.search.label});
            // Discard a popular-city pending so a subsequent manual submit doesn't fire popular_search_click.
            clearPendingAmplitudeSearch();
        }

        formActions.update(values);
    };

    const onMobileHeroChange = (values: Partial<ISearchAutocompleteFormValues>) => {
        if (shouldSelectAndCloseMobileSuggestion(values.search, mobileSelectionMode)) {
            onChange(values);
            setMobileSearchActive(false);
            return;
        }

        if (values.search && canAutoSubmitSelectedSearchValue(values.search)) {
            onMobileHeroAfterChange("search", values.search);
            return;
        }

        onChange(values);
    };

    const onAfterTabSwitch = () => {
        resetActiveDropdownItem();
    };

    const onSearchSubmit = () => {
        void onSubmit(formValues);
    };

    const isSearchButtonDisabled = !canSubmitSearchValue(formValues.search as ISearchInputValue | undefined);
    const autocompleteSubmit = isManualSubmit ? onSearchSubmit : undefined;
    const searchViewProps = {
        formValues,
        disableMultiRegion,
        autoFocus,
        mobileSelectionMode,
        fetchAllSearchLists,
        onChange,
        onMobileHeroChange,
        onAfterChange,
        onMobileHeroAfterChange,
        onAfterTabSwitch,
        onSearchSubmit,
        autocompleteSubmit,
        isSearchButtonDisabled,
        isMobileSearchActive,
        setMobileSearchActive,
        setDesktopSearchBlendActive
    };

    return (
        <>
            <DesktopBlend
                top={blendTopPosition}
                heightDecrease={containerHeight}
                isActive={isDesktopSearchBlendActive}
                pageHeight={`${windowHeight}px`}
                isViewportWidth={layoutVariant === "hero"}
            />

            {layoutVariant === "hero" ? <HomepageHeroSearch {...searchViewProps} /> : <HomepageLegacySearch {...searchViewProps} />}
        </>
    );
};

export const HomepageSearch = connect(mapStateToProps, mapActionsToProps)(HomepageSearchC);

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.search.formValues as SearchFormValuesType
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators(
            {
                resetActiveDropdownItem,
                updateCurrentTab,
                fetchAllSearchLists
            },
            dispatch
        )
    };
}

export const canSubmitSearchValue = (searchValue?: ISearchInputValue) => {
    const hasTypedUnselectedText = Boolean(searchValue?.label?.trim());

    if (!hasTypedUnselectedText) {
        return true;
    }

    switch (searchValue?.tabType) {
        case SearchTab.Regions:
            return searchValue.regions.length > 0;
        case SearchTab.Offers:
            return Boolean(searchValue.offer);
        case SearchTab.Places:
            return Boolean(searchValue.place && searchValue.location);
        case SearchTab.Vendors:
            return Boolean(searchValue.vendor);
        default:
            return false;
    }
};

export const canAutoSubmitSelectedSearchValue = (searchValue?: ISearchInputValue) => {
    switch (searchValue?.tabType) {
        case SearchTab.Regions:
            return searchValue.regions.length > 0;
        case SearchTab.Offers:
            return Boolean(searchValue.offer);
        case SearchTab.Places:
            return Boolean(searchValue.place && searchValue.location);
        case SearchTab.Vendors:
            return Boolean(searchValue.vendor);
        default:
            return false;
    }
};

const getSelectedSearchRegions = (searchValue?: ISearchInputValue) => {
    if (searchValue?.tabType === SearchTab.Regions) {
        return searchValue.regions;
    }

    return [];
};

const shouldSelectAndCloseMobileSuggestion = (searchValue: ISearchInputValue | undefined, mobileSelectionMode: HomepageSearchMobileSelectionMode) => {
    return mobileSelectionMode === "selectAndClose" && canAutoSubmitSelectedSearchValue(searchValue);
};
